/* eslint-disable prefer-const */
import KeyboardJS from "keyboardjs";
import {
  difference,
  each,
  find,
  get,
  isArray,
  isEqual,
  isNumber,
  last,
  reject,
  uniq,
} from "lodash";
import { create as createSet } from "./set";

function createStack(field, options) {
  options = options || {};
  const maxSize = options.size || -1;
  const stack = createSet(field ? [field] : null);
  const pop = (size) => {
    size = isNumber(size) ? size : 1;
    return stack.splice(stack.size() - size, size);
  };
  const add = (items) => {
    stack.add(items);
    const delta = stack.size() - maxSize;
    if (maxSize > 0 && delta > 0) {
      stack.splice(0, delta);
    }
    return stack.get();
  };
  return {
    get: stack.get,
    push: add,
    pop: pop,
    size: stack.size,
  };
}

const GLOBAL_SHORTCUTS_CONTEXT = "GLOBAL_SHORTCUTS_CONTEXT";

const _contextStack = createStack(null, { size: 4 });

let _shortcuts = [];

const keyboardBind = KeyboardJS.bind.bind(KeyboardJS);

const keyboardUnbind = KeyboardJS.unbind.bind(KeyboardJS);

// Specific keys are useful to manage chars obtain by combinaison of keys
// (to avoid issue with azerty/qwerty differences)
const specificKeys = ["?"];

const keyboardSpecificBind = (keyCode, callback) => {
  document.addEventListener("keypress", (event) => {
    const sign = String.fromCharCode(event.charCode);
    if (sign === keyCode) {
      callback(event);
    }
  });
};

const formatCallbacks = (context, callbacks) => {
  callbacks = isArray(callbacks) ? callbacks : [callbacks];
  return {
    context: context,
    callbacks: callbacks,
  };
};

const getCurrentContext = () => get(last(_contextStack.get()), "value", []);

/**
 *  Fix issue with shift, alt, ctrl combinaison key
 *  It assumes that when declaring a combinaison with those keys
 *  the order is : 'shift + ctrl + alt'
 */
const checkKeycode = (keyCode: string, event) => {
  if (!(specificKeys.indexOf(keyCode) >= 0)) {
    if (event.altKey) {
      if (keyCode.indexOf("alt") === -1) {
        keyCode = `alt + ${keyCode}`;
      }
    }
    if (event.ctrlKey) {
      if (keyCode.indexOf("ctrl") === -1) {
        keyCode = `ctrl + ${keyCode}`;
      }
    }
    if (event.shiftKey) {
      if (keyCode.indexOf("shift") === -1) {
        keyCode = `shift + ${keyCode}`;
      }
    }
  }
  return keyCode;
};

const applyDownCallback = (callbacks, event) => {
  return callbacks[0] ? callbacks[0].apply(null, [event]) : 0;
};

const applyUpCallback = (callbacks, event) =>
  callbacks[1] ? callbacks[1].apply(null, [event]) : 0;

const pressKey = (keyCode: string, context, pressDown) => (event) => {
  keyCode = checkKeycode(keyCode, event);

  const shortcut = find(_shortcuts, {
    keyCode: keyCode,
  });

  if (getCurrentContext().length && shortcut) {
    // If contexts setted execute associated callbacks
    each(getCurrentContext(), (entry) => {
      // @ts-ignore
      const finded = find(shortcut.callbacks, {
        context: entry,
      });
      if (finded) {
        if (pressDown) {
          applyDownCallback(finded.callbacks, event);
        } else {
          applyUpCallback(finded.callbacks, event);
        }
      }
    });
    // @ts-ignore
    each(shortcut.callbacks, (entry) => {
      if (entry.context === GLOBAL_SHORTCUTS_CONTEXT) {
        if (pressDown) {
          applyDownCallback(entry.callbacks, event);
        } else {
          applyUpCallback(entry.callbacks, event);
        }
      }
    });
  } else {
    // if no context and no global context setted execute all registered callbacks
    each(get(shortcut, "callbacks", null), (entry) => {
      if (pressDown) {
        applyDownCallback(entry.callbacks, event);
      } else {
        applyUpCallback(entry.callbacks, event);
      }
    });
  }
};

export function removeContext(context) {
  let newContext;
  const currentContext = getCurrentContext();
  context = isArray(context) ? context : [context];
  newContext = difference(currentContext, context);
  if (!isEqual(currentContext, newContext)) {
    _contextStack.push({ value: newContext });
  }
}

export function revertContext() {
  // @ts-ignore
  return _contextStack.pop();
}

export function setContext(context, baseContext = []) {
  let newContext;
  context = isArray(context) ? context : [context];
  newContext = uniq(baseContext.concat(context));
  if (
    !isEqual(baseContext, newContext) &&
    !isEqual(newContext, getCurrentContext())
  ) {
    _contextStack.push({ value: newContext });
  }
}

export function addContext(context) {
  return setContext(context, getCurrentContext());
}

export function removeShortcuts(context, shortcuts) {
  each(shortcuts, (callbacks, keyCode) => {
    const finded = find(_shortcuts, {
      keyCode: keyCode,
    });
    if (finded) {
      // @ts-ignore
      each(finded.callbacks, (entry) => {
        if (entry.context === context) {
          // @ts-ignore
          finded.callbacks = reject(finded.callbacks, entry);
        }
      });
      // @ts-ignore
      if (!finded.callbacks.length) {
        // @ts-ignore
        _shortcuts = reject(_shortcuts, finded);
        keyboardUnbind(keyCode);
      }
    }
  });
}

export function removeShortcutsFromContext(context) {
  each(_shortcuts, (shortcut) => {
    // @ts-ignore
    each(shortcut.callbacks, (entry) => {
      if (entry.context === context) {
        // @ts-ignore
        shortcut.callbacks = reject(shortcut.callbacks, entry);
      }
    });
    // @ts-ignore
    if (!shortcut.callbacks.length) {
      _shortcuts = reject(_shortcuts, shortcut);
      // @ts-ignore
      keyboardUnbind(shortcut.keyCode);
    }
  });
}

export function addShortcuts(context, shortcuts) {
  each(shortcuts, (callbacks, keyCode) => {
    const finded = find(_shortcuts, {
      keyCode: keyCode,
    });
    if (finded) {
      // @ts-ignore
      finded.callbacks.push(formatCallbacks(context, callbacks));
    } else {
      _shortcuts.push({
        // @ts-ignore
        keyCode: keyCode,
        // @ts-ignore
        callbacks: [formatCallbacks(context, callbacks)],
      });
      if (specificKeys.indexOf(keyCode) >= 0) {
        keyboardSpecificBind(keyCode, pressKey(keyCode, context, true));
      } else {
        keyboardBind(
          keyCode,
          pressKey(keyCode, context, true),
          pressKey(keyCode, context, false)
        );
      }
    }
  });
}

export function addGlobalShortcuts(shortcuts) {
  return addShortcuts(GLOBAL_SHORTCUTS_CONTEXT, shortcuts);
}

export function removeGlobalShortcuts(shortcuts) {
  return removeShortcuts(GLOBAL_SHORTCUTS_CONTEXT, shortcuts);
}
